/**
 * MathMarkup: a tiny TeX-like language for problem prompts. Templates emit
 * markup strings; the web renderer turns the parsed AST into HTML, and
 * toPlain/toSpeech give text for the Watch layout and screen readers.
 *
 * Grammar (complete — anything else is a parse error):
 *   digits, '.'                    number            e.g. 3.5
 *   a–z, A–Z (outside commands)    variable (italic)  x, n, a
 *   + - = < > ( ) , ; : | ' ! ?    operators/punct.  ('-' renders as U+2212 minus)
 *   space                          spacing
 *   ^{...} or ^c                   superscript (attached to what precedes)
 *   _{...} or _c                   subscript
 *   \frac{num}{den}                stacked fraction
 *   \sqrt{x}  \sqrt[n]{x}          root
 *   \box                           the answer blank □
 *   \text{words}                   upright text (no commands inside)
 *   \log \sin \cos \tan            upright function names
 *   \times \div \cdot \le \ge \ne \to \Rightarrow \pm \dots  → × ÷ · ≤ ≥ ≠ → ⇒ ± …
 *   \Delta \Sigma \pi              Δ Σ π
 *   \sep                           wide separator between two statements
 *                                  (simultaneous equations, "given" clauses)
 *
 * Unicode × ÷ − ≤ ≥ are also accepted directly.
 */

export type MathMarkup = string;

export type MNode =
  | { t: 'num'; v: string }
  | { t: 'var'; v: string }
  | { t: 'op'; v: string }
  | { t: 'text'; v: string }
  | { t: 'fn'; v: string }
  | { t: 'sym'; v: string }
  | { t: 'space' }
  | { t: 'sep' }
  | { t: 'box' }
  | { t: 'sup'; c: MNode[] }
  | { t: 'sub'; c: MNode[] }
  | { t: 'frac'; num: MNode[]; den: MNode[] }
  | { t: 'sqrt'; idx: MNode[] | null; c: MNode[] };

const OPS: Record<string, string> = {
  '+': '+', '-': '−', '−': '−', '=': '=', '<': '<', '>': '>', '(': '(', ')': ')', ',': ',', ';': ';',
  ':': ':', '|': '|', "'": '′', '!': '!', '?': '?', '×': '×', '÷': '÷', '≤': '≤', '≥': '≥', '·': '·',
};
const CMD_OPS: Record<string, string> = {
  times: '×', div: '÷', cdot: '·', le: '≤', ge: '≥', ne: '≠', to: '→', Rightarrow: '⇒', pm: '±', dots: '…',
};
const CMD_SYMS: Record<string, string> = { Delta: 'Δ', Sigma: 'Σ', pi: 'π' };
const CMD_FNS = new Set(['log', 'sin', 'cos', 'tan']);

export class MarkupError extends Error {}

export function parseMarkup(src: MathMarkup): MNode[] {
  let i = 0;

  const fail = (msg: string): never => {
    throw new MarkupError(`${msg} at ${i} in "${src}"`);
  };

  function group(): MNode[] {
    if (src[i] !== '{') fail('expected {');
    i++;
    const nodes = seq('}');
    if (src[i] !== '}') fail('expected }');
    i++;
    return nodes;
  }

  function rawGroup(): string {
    if (src[i] !== '{') fail('expected {');
    const end = src.indexOf('}', i);
    if (end < 0) fail('unclosed {');
    const s = src.slice(i + 1, end);
    i = end + 1;
    return s;
  }

  /** Argument of ^ or _: either {group} or a single char/command. */
  function script(): MNode[] {
    if (src[i] === '{') return group();
    const one = atom();
    if (!one) fail('expected script argument');
    return [one!];
  }

  function atom(): MNode | null {
    const ch = src[i];
    if (ch === undefined) return null;
    if (ch === ' ') {
      while (src[i] === ' ') i++;
      return { t: 'space' };
    }
    if (/[0-9.]/.test(ch)) {
      let v = '';
      while (i < src.length && /[0-9.]/.test(src[i]!)) v += src[i++];
      return { t: 'num', v };
    }
    if (/[a-zA-Z]/.test(ch)) {
      i++;
      return { t: 'var', v: ch };
    }
    if (ch === '□') {
      i++;
      return { t: 'box' };
    }
    if (OPS[ch]) {
      i++;
      return { t: 'op', v: OPS[ch]! };
    }
    if (ch === '\\') {
      i++;
      let name = '';
      while (i < src.length && /[a-zA-Z]/.test(src[i]!)) name += src[i++];
      if (!name) fail('empty command');
      if (name === 'frac') return { t: 'frac', num: group(), den: group() };
      if (name === 'sqrt') {
        let idx: MNode[] | null = null;
        if (src[i] === '[') {
          const end = src.indexOf(']', i);
          if (end < 0) fail('unclosed [');
          idx = parseMarkup(src.slice(i + 1, end));
          i = end + 1;
        }
        return { t: 'sqrt', idx, c: group() };
      }
      if (name === 'box') return { t: 'box' };
      if (name === 'sep') return { t: 'sep' };
      if (name === 'text') return { t: 'text', v: rawGroup() };
      if (CMD_FNS.has(name)) return { t: 'fn', v: name };
      if (CMD_OPS[name]) return { t: 'op', v: CMD_OPS[name]! };
      if (CMD_SYMS[name]) return { t: 'sym', v: CMD_SYMS[name]! };
      fail(`unknown command \\${name}`);
    }
    fail(`unexpected character "${ch}"`);
    return null;
  }

  function seq(close?: string): MNode[] {
    const out: MNode[] = [];
    while (i < src.length && src[i] !== close) {
      const ch = src[i];
      if (ch === '}') fail('unbalanced }');
      if (ch === '^' || ch === '_') {
        i++;
        if (!out.length) fail(`${ch} with nothing before it`);
        out.push(ch === '^' ? { t: 'sup', c: script() } : { t: 'sub', c: script() });
        continue;
      }
      const a = atom();
      if (a) out.push(a);
    }
    return out;
  }

  const nodes = seq();
  if (i !== src.length) fail('trailing input');
  return nodes;
}

/** Throws MarkupError if the markup is invalid. */
export function validateMarkup(src: MathMarkup): void {
  parseMarkup(src);
}

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '−': '⁻',
};

function plain(nodes: MNode[]): string {
  let s = '';
  for (const n of nodes) {
    switch (n.t) {
      case 'num': case 'var': case 'op': case 'text': case 'fn': case 'sym':
        s += n.v;
        break;
      case 'space': s += ' '; break;
      case 'sep': s += ';  '; break;
      case 'box': s += '□'; break;
      case 'sup': {
        const inner = plain(n.c);
        s += [...inner].every((c) => SUPERSCRIPT_DIGITS[c]) ? [...inner].map((c) => SUPERSCRIPT_DIGITS[c]).join('') : `^(${inner})`;
        break;
      }
      case 'sub': s += `_${plain(n.c)}`; break;
      case 'frac': {
        const a = plain(n.num);
        const b = plain(n.den);
        const wrap = (x: string) => (/^[\w□.]+$/u.test(x) ? x : `(${x})`);
        s += `${wrap(a)}/${wrap(b)}`;
        break;
      }
      case 'sqrt': s += `${n.idx ? plain(n.idx) : ''}√(${plain(n.c)})`; break;
    }
  }
  return s;
}

/** Single-line Unicode rendering, e.g. "x² + 5x + 6 = (x + 2)(x + □)". */
export function toPlain(src: MathMarkup): string {
  return plain(parseMarkup(src)).replace(/ {2,}/g, ' ').trim();
}

export type SpeechLocale = 'en' | 'af';

/**
 * Spoken words per locale. Keys are rendered operator glyphs plus these words:
 * `sub` (a subscript that is only a label: T sub n / T onder n), `base` (only
 * after log), `inverse` (f^{-1} before a bracket), and `sumFrom`/`sumTo`/
 * `sumOf`/`boundEq` for Σ with bounds ("the sum from k equals 1 to 4 of").
 * `neClose` closes a negated relation at the end of its statement: Afrikaans
 * negates twice, "x is nie gelyk aan 3 nie".
 */
const SPEECH: Record<SpeechLocale, Record<string, string>> = {
  en: {
    '+': 'plus', '−': 'minus', '=': 'equals', '×': 'times', '÷': 'divided by', '·': 'times', '<': 'is less than',
    '>': 'is greater than', '≤': 'is at most', '≥': 'is at least', '≠': 'is not equal to', '(': 'open bracket',
    ')': 'close bracket', box: 'blank', squared: 'squared', cubed: 'cubed', pow: 'to the power of', over: 'over',
    sqrt: 'square root of', root: 'root of', base: 'base', sep: 'and', '→': 'gives', '⇒': 'so', '±': 'plus or minus',
    '…': 'and so on', ',': ',', ';': ';', sub: 'sub', inverse: 'inverse', sumFrom: 'the sum from', sumTo: 'to',
    sumOf: 'of', boundEq: 'equals',
  },
  af: {
    '+': 'plus', '−': 'minus', '=': 'is gelyk aan', '×': 'maal', '÷': 'gedeel deur', '·': 'maal', '<': 'is kleiner as',
    '>': 'is groter as', '≤': 'is hoogstens', '≥': 'is minstens', '≠': 'is nie gelyk aan', '(': 'hakie oop',
    ')': 'hakie toe', box: 'leë blokkie', squared: 'kwadraat', cubed: 'tot die derde mag', pow: 'tot die mag',
    over: 'oor', sqrt: 'vierkantswortel van', root: 'wortel van', base: 'basis', sep: 'en', '→': 'gee', '⇒': 'dus',
    '±': 'plus of minus', '…': 'ensovoorts', ',': ',', ';': ';', sub: 'onder', inverse: 'invers',
    sumFrom: 'die som van', sumTo: 'tot', sumOf: 'van', boundEq: 'gelyk aan', neClose: 'nie',
  },
};

/** The next node after `i` that is not a space. */
function nextSolid(nodes: readonly MNode[], i: number): MNode | undefined {
  for (let j = i + 1; j < nodes.length; j++) if (nodes[j]!.t !== 'space') return nodes[j];
  return undefined;
}

/** A Σ bound, with its "=" read as a plain "equals" (k equals 1), not as a statement. */
function bound(nodes: MNode[], L: Record<string, string>): string[] {
  return nodes.flatMap((n) => (n.t === 'op' && n.v === '=' ? [L.boundEq!] : speech([n], L)));
}

/** Operators that end one side of a statement (where a pending `neClose` is spoken). */
const RELATIONS = new Set(['=', '<', '>', '≤', '≥', '≠']);

function speech(nodes: MNode[], L: Record<string, string>): string[] {
  const out: string[] = [];
  let close: string | null = null; // Afrikaans "nie" still owed by a "≠" in this statement
  const flush = () => {
    if (close) out.push(close);
    close = null;
  };
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]!;
    const prev = i > 0 ? nodes[i - 1] : undefined;
    if (n.t === 'sep' || (n.t === 'op' && RELATIONS.has(n.v))) flush();
    switch (n.t) {
      case 'num': out.push(n.v); break;
      case 'var': out.push(n.v); break;
      case 'op':
        out.push(L[n.v] ?? n.v);
        if (n.v === '≠' && L.neClose) close = L.neClose;
        break;
      case 'sym': {
        // Σ_{k=1}^{4} → "the sum from k equals 1 to 4 of" (the two bounds may come in either order).
        let lower: MNode[] | null = null;
        let upper: MNode[] | null = null;
        while (n.v === 'Σ' && i + 1 < nodes.length) {
          const s = nodes[i + 1]!;
          if (s.t === 'sub' && !lower) lower = s.c;
          else if (s.t === 'sup' && !upper) upper = s.c;
          else break;
          i++;
        }
        if (!lower && !upper) {
          out.push(n.v);
          break;
        }
        out.push(L.sumFrom!);
        if (lower) out.push(...bound(lower, L));
        if (upper) out.push(L.sumTo!, ...bound(upper, L));
        out.push(L.sumOf!);
        break;
      }
      case 'text': case 'fn': out.push(n.v); break;
      case 'space': break;
      case 'sep': out.push(L.sep!); break;
      case 'box': out.push(L.box!); break;
      case 'sup': {
        const p = plain(n.c);
        const after = nextSolid(nodes, i);
        if (p === '−1' && prev?.t === 'var' && after?.t === 'op' && after.v === '(') {
          out.push(L.inverse!); // f^{-1}(16): the inverse function, not a power
        } else if (p === '2') out.push(L.squared!);
        else if (p === '3') out.push(L.cubed!);
        else out.push(L.pow!, ...speech(n.c, L));
        break;
      }
      case 'sub':
        // log_2 8 → "log base 2 8"; any other subscript is a label: T_n → "T sub n".
        out.push(prev?.t === 'fn' && prev.v === 'log' ? L.base! : L.sub!, ...speech(n.c, L));
        break;
      case 'frac': out.push(...speech(n.num, L), L.over!, ...speech(n.den, L)); break;
      case 'sqrt':
        if (n.idx) out.push(...speech(n.idx, L), L.root!);
        else out.push(L.sqrt!);
        out.push(...speech(n.c, L));
        break;
    }
  }
  flush();
  return out;
}

/** Screen-reader text, e.g. "x squared plus 5 x plus 6 equals ...". */
export function toSpeech(src: MathMarkup, locale: SpeechLocale = 'en'): string {
  return speech(parseMarkup(src), SPEECH[locale]).join(' ').replace(/\s+/g, ' ').trim();
}
